import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { localeTag } from '@/i18n/format';
/**
 * BIComponents — Mini BI toolkit for CatchmentResultPanel fullscreen mode.
 *
 * Provides:
 *  • DraggableCard    — drag + resize any content block within a bounded container
 *  • SelectableTable  — data table with row/col checkboxes
 *  • ChartTypeModal   — pick chart type, set title, configure, preview (create + edit)
 *  • BIChart          — recharts wrapper supporting 10 chart types
 *  • KPICard          — single-value indicator / gauge
 */
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useColumnTranslations, useLayerTranslations } from '../../hooks/useColumnTranslations';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, ZAxis,
  RadialBarChart, RadialBar,
  FunnelChart, Funnel, LabelList,
} from 'recharts';
import {
  X, GripVertical, BarChart2, TrendingUp, PieChart as PieIcon, Layers,
  Check, Plus, Trash2, Maximize2, Minimize2, Edit3, Target, Activity,
  Hash, GitBranch, Gauge, CircleDot,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════ */

export type ChartType =
  | 'bar' | 'stackedBar' | 'horizontalBar'
  | 'line' | 'area'
  | 'pie' | 'donut'
  | 'radar'
  | 'scatter'
  | 'kpi'
  | 'funnel'
  | 'radialBar';

export interface CardPosition {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ChartConfig {
  id: string;
  type: ChartType;
  title: string;
  data: Record<string, any>[];
  dataKeys: string[];
  categoryKey: string;
  colors: string[];
  /** For KPI — which key to show as main value */
  kpiKey?: string;
  /** For KPI — subtitle text */
  kpiSubtitle?: string;
  /** Source table + selection so we can re-edit */
  sourceTableId?: string;
  sourceRows?: number[];
  sourceCols?: string[];
}

export interface SelectionState {
  tableId: string;
  selectedRows: Set<number>;
  selectedCols: Set<string>;
}

/* ═══════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════ */

/** Strip common formatting prefixes/suffixes (%, commas, spaces) and parse as number */
function parseNumeric(raw: any): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  const s = String(raw).replace(/[%,\s]/g, '').trim();
  if (s === '') return null;
  const n = Number(s);
  return isNaN(n) ? null : n;
}

const CHART_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

/* ═══════════════════════════════════════════════════════
   Chart type definitions
   ═══════════════════════════════════════════════════════ */

interface ChartTypeDef {
  type: ChartType;
  icon: React.ReactNode;
  label: string;
  group: string;
}

const CHART_TYPE_DEFS: ChartTypeDef[] = [
  { type: 'bar', icon: <BarChart2 size={14} />, label: 'bi.type_bar', group: 'bi.group_basic' },
  { type: 'stackedBar', icon: <Layers size={14} />, label: 'bi.type_stackedBar', group: 'bi.group_basic' },
  { type: 'horizontalBar', icon: <BarChart2 size={14} className="rotate-90" />, label: 'bi.type_horizontalBar', group: 'bi.group_basic' },
  { type: 'line', icon: <TrendingUp size={14} />, label: 'bi.type_line', group: 'bi.group_basic' },
  { type: 'area', icon: <Activity size={14} />, label: 'bi.type_area', group: 'bi.group_basic' },
  { type: 'pie', icon: <PieIcon size={14} />, label: 'bi.type_pie', group: 'bi.group_distribution' },
  { type: 'donut', icon: <CircleDot size={14} />, label: 'bi.type_donut', group: 'bi.group_distribution' },
  { type: 'funnel', icon: <GitBranch size={14} />, label: 'bi.type_funnel', group: 'bi.group_distribution' },
  { type: 'radar', icon: <Target size={14} />, label: 'bi.type_radar', group: 'bi.group_advanced' },
  { type: 'scatter', icon: <Hash size={14} />, label: 'bi.type_scatter', group: 'bi.group_advanced' },
  { type: 'radialBar', icon: <Gauge size={14} />, label: 'bi.type_radialBar', group: 'bi.group_advanced' },
  { type: 'kpi', icon: <Hash size={14} />, label: 'bi.type_kpi', group: 'bi.group_indicator' },
];

/* ═══════════════════════════════════════════════════════
   DraggableCard — bounded drag + resize within a parent
   ═══════════════════════════════════════════════════════ */

interface DraggableCardProps {
  pos: CardPosition;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, w: number, h: number) => void;
  onRemove?: (id: string) => void;
  onEdit?: (id: string) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  title?: string;
  children: React.ReactNode;
  headerExtra?: React.ReactNode;
  minW?: number;
  minH?: number;
}

export const DraggableCard: React.FC<DraggableCardProps> = ({
  pos, onMove, onResize, onRemove, onEdit, containerRef, title, children, headerExtra,
  minW = 200, minH = 120,
}) => {
  const { t } = useTranslation();
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const resRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);

  // Keep latest callbacks in refs so drag/resize handlers never go stale
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  const posRef = useRef(pos);
  posRef.current = pos;

  const clampX = useCallback((x: number, w: number) => {
    const cont = containerRef.current;
    if (!cont) return x;
    // Only clamp horizontally — vertical is unlimited (scrollable)
    return Math.max(0, Math.min(x, cont.scrollWidth - w));
  }, [containerRef]);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
    e.preventDefault();
    const currentPos = posRef.current;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: currentPos.x, origY: currentPos.y };

    const onMv = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const nx = dragRef.current.origX + ev.clientX - dragRef.current.startX;
      const ny = dragRef.current.origY + ev.clientY - dragRef.current.startY;
      const cx = clampX(nx, posRef.current.w);
      const cy = Math.max(0, ny); // no negative Y, but no upper cap
      onMoveRef.current(posRef.current.id, cx, cy);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMv);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMv);
    window.addEventListener('mouseup', onUp);
  }, [clampX]);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const currentPos = posRef.current;
    resRef.current = { startX: e.clientX, startY: e.clientY, origW: currentPos.w, origH: currentPos.h };
    const onMv = (ev: MouseEvent) => {
      if (!resRef.current) return;
      const nw = Math.max(minW, resRef.current.origW + ev.clientX - resRef.current.startX);
      const nh = Math.max(minH, resRef.current.origH + ev.clientY - resRef.current.startY);
      onResizeRef.current(posRef.current.id, nw, nh);
    };
    const onUp = () => { resRef.current = null; window.removeEventListener('mousemove', onMv); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMv);
    window.addEventListener('mouseup', onUp);
  }, [minW, minH]);

  return (
    <div
      className="absolute flex flex-col bg-gn-secondary border border-gn rounded-lg shadow-xl overflow-hidden"
      style={{ left: pos.x, top: pos.y, width: pos.w, height: pos.h }}
    >
      {/* Header — draggable */}
      <div
        className="flex items-center justify-between px-2 py-1.5 bg-gn-tertiary shrink-0 cursor-move select-none border-b border-gn-light"
        onMouseDown={onDragStart}
      >
        <span className="flex items-center gap-1.5 text-[10px] font-semibold text-gn-primary truncate">
          <GripVertical size={10} className="text-gn-secondary" />
          {title || pos.id}
        </span>
        <div className="flex items-center gap-0.5" data-no-drag>
          {headerExtra}
          {onEdit && (
            <button onClick={() => onEdit(pos.id)} className="p-0.5 text-gn-secondary hover:text-gn-blue transition-colors" title={t('buttons.edit')}>
              <Edit3 size={11} />
            </button>
          )}
          {onRemove && (
            <button onClick={() => onRemove(pos.id)} className="p-0.5 text-gn-secondary hover:text-gn-danger transition-colors" title={t('bi.remove')}>
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
      {/* Body */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
      {/* Resize handle */}
      <div
        className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize"
        onMouseDown={onResizeStart}
        style={{
          background: 'linear-gradient(135deg, transparent 50%, rgba(100,116,139,0.5) 50%)',
        }}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   SelectableTable — data table with row/col checkboxes
   ═══════════════════════════════════════════════════════ */

interface SelectableTableProps {
  tableId: string;
  keys: string[];
  features: Record<string, any>[];
  layerName: string;
  selection: SelectionState | null;
  onSelectionChange: (sel: SelectionState) => void;
  onCreateChart: () => void;
}

export const SelectableTable: React.FC<SelectableTableProps> = ({
  tableId, keys, features, layerName, selection, onSelectionChange, onCreateChart,
}) => {
  const { t } = useTranslation();
  const { t: tCol } = useColumnTranslations();
  const { t: tLayer } = useLayerTranslations();
  const visibleKeys = useMemo(() => keys.filter((k) => k !== 'geom'), [keys]);
  const selRows = selection?.selectedRows ?? new Set<number>();
  const selCols = selection?.selectedCols ?? new Set<string>();

  const toggleRow = (idx: number) => {
    const next = new Set(selRows);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    onSelectionChange({ tableId, selectedRows: next, selectedCols: selCols });
  };

  const toggleCol = (col: string) => {
    const next = new Set(selCols);
    if (next.has(col)) next.delete(col); else next.add(col);
    onSelectionChange({ tableId, selectedRows: selRows, selectedCols: next });
  };

  const selectAll = () => {
    const allRows = new Set(features.map((_, i) => i));
    onSelectionChange({ tableId, selectedRows: allRows, selectedCols: selCols });
  };

  const hasSelection = selRows.size > 0 && selCols.size > 0;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-2 py-1 bg-gn-tertiary border-b border-gn-light shrink-0">
        <span className="text-[10px] text-gn-primary font-medium">{tLayer(layerName)}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-gn-secondary">{features.length} rows</span>
          {selCols.size > 0 && selRows.size === 0 && (
            <button
              onClick={selectAll}
              className="px-1.5 py-0.5 text-[9px] rounded bg-gn-hover text-gn-primary hover:bg-gn-hover transition-colors"
            >
              {t('bi.select_all_rows')}
            </button>
          )}
          {hasSelection && (
            <button
              onClick={onCreateChart}
              className="flex items-center gap-1 px-2 py-0.5 text-[9px] rounded bg-gn-blue text-gn-inverse hover:bg-gn-blue transition-colors"
            >
              <Plus size={10} /> {t('bi.chart')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-[9px] border-collapse">
          <thead className="sticky top-0 z-[1]">
            <tr className="bg-gn-tertiary">
              <th className="px-1 py-1 text-gn-secondary w-6">#</th>
              {visibleKeys.map((k) => (
                <th key={k} className="px-1.5 py-1 text-left font-medium whitespace-nowrap">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selCols.has(k)}
                      onChange={() => toggleCol(k)}
                      className="rounded accent-blue-500 w-2.5 h-2.5"
                    />
                    <span className={selCols.has(k) ? 'text-gn-blue' : 'text-gn-secondary'}>{tCol(k)}</span>
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map((feat, fi) => {
              const isSelected = selRows.has(fi);
              return (
                <tr
                  key={fi}
                  className={`border-t border-gn-light hover:bg-gn-tertiary cursor-pointer ${isSelected ? 'bg-gn-blue-light' : ''}`}
                  onClick={() => toggleRow(fi)}
                >
                  <td className="px-1 py-0.5 text-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRow(fi)}
                      className="rounded accent-blue-500 w-2.5 h-2.5"
                    />
                  </td>
                  {visibleKeys.map((k) => (
                    <td
                      key={k}
                      className={`px-1.5 py-0.5 whitespace-nowrap ${selCols.has(k) ? 'text-gn-blue bg-gn-blue-light' : 'text-gn-primary'}`}
                    >
                      {feat[k] != null ? String(feat[k]) : ''}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   ChartTypeModal — create or edit chart
   ═══════════════════════════════════════════════════════ */

interface ChartTypeModalProps {
  selection: SelectionState;
  keys: string[];
  features: Record<string, any>[];
  onConfirm: (config: Omit<ChartConfig, 'id'>) => void;
  onCancel: () => void;
  /** When editing, pass the existing chart config */
  editingChart?: ChartConfig | null;
}

export const ChartTypeModal: React.FC<ChartTypeModalProps> = ({
  selection, keys, features, onConfirm, onCancel, editingChart,
}) => {
  const { t } = useTranslation();
  const isEdit = !!editingChart;
  const [chartType, setChartType] = useState<ChartType>(editingChart?.type ?? 'bar');
  const [title, setTitle] = useState(editingChart?.title ?? '');
  const [categoryKey, setCategoryKey] = useState(editingChart?.categoryKey === 'name' ? '' : (editingChart?.categoryKey ?? ''));
  const [kpiKey, setKpiKey] = useState(editingChart?.kpiKey ?? '');

  const visibleKeys = useMemo(() => keys.filter((k) => k !== 'geom'), [keys]);
  const selectedCols = Array.from(selection.selectedCols);
  const selectedRows = Array.from(selection.selectedRows);

  // Auto-pick category: first non-numeric selected column, or first column
  useEffect(() => {
    if (isEdit && editingChart?.sourceCols) return; // keep existing for edits
    const firstNonNumeric = selectedCols.find((col) => {
      return selectedRows.some((ri) => {
        const v = features[ri]?.[col];
        return v != null && parseNumeric(v) === null;
      });
    });
    setCategoryKey(firstNonNumeric || selectedCols[0] || visibleKeys[0] || '');
  }, [selectedCols.join(','), selectedRows.join(',')]);

  // Auto-pick kpiKey
  useEffect(() => {
    if (kpiKey) return;
    const numCols = selectedCols.filter((c) => c !== categoryKey);
    if (numCols.length > 0) setKpiKey(numCols[0]);
  }, [categoryKey, selectedCols.join(',')]);

  const numericCols = selectedCols.filter((c) => c !== categoryKey);

  // Build chart data from selection
  const chartData = useMemo(() => {
    return selectedRows.map((ri) => {
      const row: Record<string, any> = {};
      row.name = String(features[ri]?.[categoryKey] ?? `Row ${ri + 1}`);
      numericCols.forEach((col) => {
        const raw = features[ri]?.[col];
        row[col] = parseNumeric(raw) ?? 0;
      });
      return row;
    });
  }, [selectedRows, categoryKey, numericCols, features]);

  // Auto-generate title if empty
  const effectiveTitle = title || (chartType === 'kpi' ? (kpiKey || 'KPI') : `${categoryKey} — ${t(CHART_TYPE_DEFS.find((d) => d.type === chartType)?.label ?? chartType)}`);

  const handleConfirm = () => {
    onConfirm({
      type: chartType,
      title: effectiveTitle,
      data: chartData,
      dataKeys: numericCols,
      categoryKey: 'name',
      colors: CHART_COLORS.slice(0, Math.max(numericCols.length, chartData.length)),
      kpiKey: chartType === 'kpi' ? kpiKey : undefined,
      kpiSubtitle: chartType === 'kpi' ? categoryKey : undefined,
      sourceTableId: selection.tableId,
      sourceRows: selectedRows,
      sourceCols: selectedCols,
    });
  };

  // Group chart types for display
  const groups = useMemo(() => {
    const g: Record<string, ChartTypeDef[]> = {};
    CHART_TYPE_DEFS.forEach((ct) => {
      (g[ct.group] ??= []).push(ct);
    });
    return g;
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gn-primary border border-gn rounded-xl shadow-2xl w-[680px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gn">
          <span className="text-sm font-semibold text-gn-primary">
            {isEdit ? t('bi.edit_chart') : t('bi.create_chart')}
          </span>
          <button onClick={onCancel} className="p-1 text-gn-secondary hover:text-gn-primary"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-4">

          {/* Title input */}
          <div>
            <label className="text-[10px] text-gn-secondary font-medium block mb-1">{t('bi.chart_title')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={effectiveTitle}
              className="w-full px-2 py-1.5 text-xs text-gn-primary bg-gn-secondary border border-gn rounded focus:border-gn-blue focus:outline-none placeholder-gn-muted"
            />
          </div>

          {/* Chart type selector — grouped */}
          <div>
            <label className="text-[10px] text-gn-secondary font-medium block mb-1.5">{t('bi.chart_type')}</label>
            {Object.entries(groups).map(([groupName, types]) => (
              <div key={groupName} className="mb-2">
                <span className="text-[9px] text-gn-secondary uppercase tracking-wider">{t(groupName)}</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {types.map((ct) => (
                    <button
                      key={ct.type}
                      onClick={() => setChartType(ct.type)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] transition-colors ${
                        chartType === ct.type
                          ? 'border-gn-blue bg-gn-blue-light text-gn-blue'
                          : 'border-gn bg-gn-secondary text-gn-secondary hover:border-gn'
                      }`}
                    >
                      {ct.icon}
                      {t(ct.label)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Category key selector */}
          <div>
            <label className="text-[10px] text-gn-secondary font-medium block mb-1">{t('bi.category_axis')}</label>
            <select
              value={categoryKey}
              onChange={(e) => setCategoryKey(e.target.value)}
              className="w-full px-2 py-1.5 text-xs text-gn-primary bg-gn-secondary border border-gn rounded focus:border-gn-blue focus:outline-none"
            >
              {selectedCols.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* KPI key selector — only for kpi type */}
          {chartType === 'kpi' && (
            <div>
              <label className="text-[10px] text-gn-secondary font-medium block mb-1">{t('bi.kpi_column')}</label>
              <select
                value={kpiKey}
                onChange={(e) => setKpiKey(e.target.value)}
                className="w-full px-2 py-1.5 text-xs text-gn-primary bg-gn-secondary border border-gn rounded focus:border-gn-blue focus:outline-none"
              >
                {numericCols.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          {/* Data series */}
          <div>
            <label className="text-[10px] text-gn-secondary font-medium block mb-1">
              {t('bi.value_series', { count: numericCols.length })}
            </label>
            <div className="flex flex-wrap gap-1">
              {numericCols.map((col, i) => (
                <span
                  key={col}
                  className="px-2 py-0.5 text-[10px] rounded-full border"
                  style={{ borderColor: CHART_COLORS[i % CHART_COLORS.length], color: CHART_COLORS[i % CHART_COLORS.length] }}
                >
                  {col}
                </span>
              ))}
              {numericCols.length === 0 && chartType !== 'kpi' && (
                <span className="text-[10px] text-gn-warning">{t('bi.need_numeric')}</span>
              )}
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="text-[10px] text-gn-secondary font-medium block mb-1">{t('bi.preview')}</label>
            <div className="h-52 bg-gn-secondary rounded-lg border border-gn-light p-2">
              {chartData.length > 0 && (numericCols.length > 0 || chartType === 'kpi') ? (
                <BIChart
                  config={{
                    id: 'preview',
                    type: chartType,
                    title: '',
                    data: chartData,
                    dataKeys: numericCols,
                    categoryKey: 'name',
                    colors: CHART_COLORS.slice(0, Math.max(numericCols.length, chartData.length)),
                    kpiKey,
                    kpiSubtitle: categoryKey,
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-[11px] text-gn-secondary">
                  {t('bi.preview_hint')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gn">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs rounded border border-gn text-gn-primary hover:bg-gn-tertiary transition-colors"
          >
            {t('buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={chartType !== 'kpi' && (numericCols.length === 0 || chartData.length === 0)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-gn-blue text-gn-inverse hover:bg-gn-blue disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Check size={12} /> {isEdit ? t('bi.update_chart') : t('bi.add_chart')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   BIChart — renders a recharts chart from ChartConfig
   ═══════════════════════════════════════════════════════ */

interface BIChartProps {
  config: ChartConfig;
}

export const BIChart: React.FC<BIChartProps> = ({ config }) => {
  const { t } = useTranslation();
  const { type, data, dataKeys, categoryKey, colors } = config;

  const tooltipStyle = {
    contentStyle: { background: 'var(--gn-bg-secondary)', border: '1px solid var(--gn-border)', borderRadius: 8, fontSize: 10 },
    itemStyle: { color: 'var(--gn-text-secondary)' },
  };
  const tickStyle = { fontSize: 9, fill: 'var(--gn-text-muted)' };

  /* ── KPI Card ── */
  if (type === 'kpi') {
    const key = config.kpiKey || dataKeys[0];
    if (!key || data.length === 0) return <div className="flex items-center justify-center h-full text-gn-secondary text-[10px]">{t('messages.no_data')}</div>;

    const values = data.map((d) => parseNumeric(d[key]) ?? 0);
    const total = values.reduce((a, b) => a + b, 0);
    const avg = values.length > 0 ? total / values.length : 0;
    const max = Math.max(...values);
    const min = Math.min(...values);

    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 px-4">
        <div className="text-3xl font-bold text-gn-blue">{total.toLocaleString(localeTag(), { maximumFractionDigits: 2 })}</div>
        <div className="text-[10px] text-gn-secondary font-medium">{config.kpiSubtitle || key} — Total</div>
        <div className="flex gap-4 mt-1">
          <div className="text-center">
            <div className="text-sm font-semibold text-gn-success">{avg.toLocaleString(localeTag(), { maximumFractionDigits: 2 })}</div>
            <div className="text-[9px] text-gn-secondary">{t('stats.avg')}</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold text-gn-warning">{max.toLocaleString(localeTag(), { maximumFractionDigits: 2 })}</div>
            <div className="text-[9px] text-gn-secondary">{t('stats.max')}</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold text-gn-danger">{min.toLocaleString(localeTag(), { maximumFractionDigits: 2 })}</div>
            <div className="text-[9px] text-gn-secondary">{t('stats.min')}</div>
          </div>
        </div>
        {/* Sparkline */}
        {values.length > 1 && (
          <div className="w-full h-12 mt-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <Area type="monotone" dataKey={key} stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  }

  /* ── Pie / Donut ── */
  if (type === 'pie' || type === 'donut') {
    const dk = dataKeys[0];
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey={dk}
            nameKey={categoryKey}
            cx="50%"
            cy="50%"
            innerRadius={type === 'donut' ? '45%' : 0}
            outerRadius="80%"
            label={({ name, value }) => `${name}: ${typeof value === 'number' ? value.toLocaleString(localeTag()) : value}`}
            labelLine={false}
            fontSize={9}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  /* ── Funnel ── */
  if (type === 'funnel') {
    const dk = dataKeys[0];
    const funnelData = data.map((d, i) => ({ ...d, fill: CHART_COLORS[i % CHART_COLORS.length] }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <FunnelChart>
          <Tooltip {...tooltipStyle} />
          <Funnel dataKey={dk} data={funnelData} isAnimationActive>
            <LabelList position="right" fill="var(--gn-text-secondary)" fontSize={9} dataKey="name" />
          </Funnel>
        </FunnelChart>
      </ResponsiveContainer>
    );
  }

  /* ── Radar ── */
  if (type === 'radar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
          <PolarGrid stroke="var(--gn-border)" />
          <PolarAngleAxis dataKey={categoryKey} tick={tickStyle} />
          <PolarRadiusAxis tick={tickStyle} />
          {dataKeys.map((dk, i) => (
            <Radar
              key={dk}
              name={dk}
              dataKey={dk}
              stroke={colors[i % colors.length]}
              fill={colors[i % colors.length]}
              fillOpacity={0.2}
            />
          ))}
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </RadarChart>
      </ResponsiveContainer>
    );
  }

  /* ── Scatter ── */
  if (type === 'scatter') {
    const xKey = dataKeys[0];
    const yKey = dataKeys[1] || dataKeys[0];
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--gn-border)" />
          <XAxis dataKey={xKey} name={xKey} tick={tickStyle} type="number" />
          <YAxis dataKey={yKey} name={yKey} tick={tickStyle} type="number" />
          <ZAxis range={[40, 400]} />
          <Tooltip {...tooltipStyle} cursor={{ strokeDasharray: '3 3' }} />
          <Scatter name={`${xKey} vs ${yKey}`} data={data} fill={colors[0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  /* ── Radial Bar ── */
  if (type === 'radialBar') {
    const dk = dataKeys[0];
    const radialData = data.map((d, i) => ({ ...d, fill: CHART_COLORS[i % CHART_COLORS.length] }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={radialData} startAngle={180} endAngle={0}>
          <RadialBar
            background={{ fill: 'var(--gn-bg-secondary)' }}
            dataKey={dk}
            label={{ position: 'insideStart', fill: '#d1d5db', fontSize: 9 }}
          />
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </RadialBarChart>
      </ResponsiveContainer>
    );
  }

  /* ── Horizontal Bar ── */
  if (type === 'horizontalBar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 10, left: 40, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--gn-border)" />
          <XAxis type="number" tick={tickStyle} />
          <YAxis type="category" dataKey={categoryKey} tick={tickStyle} width={60} />
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {dataKeys.map((dk, i) => (
            <Bar key={dk} dataKey={dk} fill={colors[i % colors.length]} fillOpacity={0.8} radius={[0, 3, 3, 0] as any} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  /* ── Stacked Bar ── */
  if (type === 'stackedBar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--gn-border)" />
          <XAxis dataKey={categoryKey} tick={tickStyle} />
          <YAxis tick={tickStyle} />
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {dataKeys.map((dk, i) => (
            <Bar key={dk} dataKey={dk} stackId="stack" fill={colors[i % colors.length]} fillOpacity={0.85} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  /* ── Default: Bar / Line / Area ── */
  const ChartComponent = type === 'line' ? LineChart : type === 'area' ? AreaChart : BarChart;

  const renderSeries = (dk: string, i: number) => {
    const color = colors[i % colors.length];
    if (type === 'line') return <Line key={dk} type="monotone" dataKey={dk} stroke={color} strokeWidth={2} dot={{ r: 3 }} />;
    if (type === 'area') return <Area key={dk} type="monotone" dataKey={dk} fill={color} stroke={color} fillOpacity={0.3} />;
    return <Bar key={dk} dataKey={dk} fill={color} fillOpacity={0.8} radius={[3, 3, 0, 0] as any} />;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ChartComponent data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--gn-border)" />
        <XAxis dataKey={categoryKey} tick={tickStyle} />
        <YAxis tick={tickStyle} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {dataKeys.map((dk, i) => renderSeries(dk, i))}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

/* ═══════════════════════════════════════════════════════
   FullscreenToggle button
   ═══════════════════════════════════════════════════════ */

interface FullscreenToggleProps {
  isFullscreen: boolean;
  onToggle: () => void;
}

export const FullscreenToggle: React.FC<FullscreenToggleProps> = ({ isFullscreen, onToggle }) => (
  <button
    onClick={onToggle}
    className="p-0.5 text-gn-secondary hover:text-gn-primary transition-colors"
    title={isFullscreen ? i18n.t('report.exit_fullscreen') : i18n.t('map.fullscreen')}
  >
    {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
  </button>
);
